import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user';
import { pickTodayIncomeTotal } from '@/entities/profit';
import { Bell } from '@/entities/notification';
import { LanguageSwitcher } from '@/features/language';
import { brand } from '@/brand';
import { toggleSidebarCollapsed } from '@/features/sidebar';
import { useLogout } from '@/features/auth';
import { Icon } from '@/shared/IconCollecion';
import { Logo } from '@/shared/ui/Logo';
import useClickOutside from '@/shared/hooks/useClickOutside';
import { formatMoney } from '@/shared/lib/numberFormat';

// Header — реализация на основе mcms-front. Расхождения с фронтендером:
// 1. Chevron — кликабельный, делает sidebar collapse/expand (наша legacy-фича).
// 2. Wallets-блок и User-link сделаны dropdown-ами с содержимым из нашего
//    legacy Header.tsx (Профиль/2FA/Выйти + все wallets).
//
// ProfitInfo в variant=header больше не используем здесь — рендерим сами,
// чтобы добавить интерактивность Wallets. Компонент остаётся для green-темы
// (через Sidebar variant=menu).

function useDropdown<T extends HTMLElement>() {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);
  useClickOutside(ref, () => setOpen(false));
  return { open, setOpen, ref };
}

const Header = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const logout = useLogout();
  const walletsMenu = useDropdown<HTMLDivElement>();
  const userMenu = useDropdown<HTMLDivElement>();

  const todayIncomes = formatMoney(pickTodayIncomeTotal(user?.today_incomes));
  const wallets = user?.wallets ?? [];
  const firstWallet = wallets[0];

  const handleLogout = () => {
    userMenu.setOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const handleNavigate = (path: string, closeMenu: () => void) => () => {
    closeMenu();
    navigate(path);
  };

  const { t } = useTranslation(['common', 'nav']);

  return (
    <header className="bg-surface-sidebar h-header-size sticky top-0 z-50 hidden select-none items-center justify-between pl-4 lg:flex">
      <div className="flex items-center">
        <button
          type="button"
          aria-label={t('toggleMenu')}
          className="flex h-8 w-8 items-center justify-center text-content-inverse-muted hover:text-content-inverse-accent"
          onClick={() => dispatch(toggleSidebarCollapsed())}
        >
          <Icon name="Chevron" color="currentColor" />
        </button>
        <Link to="/" className="ml-4">
          <Logo min={false} />
        </Link>
      </div>

      <div className="flex h-full">
        {/* Язык и уведомления — одна ячейка: обе иконки про кабинет целиком,
            а не про деньги в соседних блоках. Свитчер живёт снаружи флага
            notifications: бренд может выключить колокольчик, язык остаётся. */}
        <div className="border-line-muted flex h-full items-center gap-4 border-r px-6">
          {brand.features.notifications && <Bell />}
          <LanguageSwitcher />
        </div>

        {/* «За сегодня» — простой текст, без интерактива (1:1 с legacy). */}
        <div className="border-line-muted flex flex-col items-start justify-center border-r px-6">
          <span className="font-bold text-content-inverse-muted">{todayIncomes} ₽</span>
          <span className="text-xs font-normal leading-4 text-content-inverse-accent">{t('profit.today')}</span>
        </div>

        {/* Wallets dropdown — клик показывает список всех кошельков. */}
        {wallets.length > 0 && firstWallet && (
          <div
            ref={walletsMenu.ref}
            className="border-line-muted relative flex h-full items-center border-r px-6"
          >
            <button
              type="button"
              className="flex flex-col items-start justify-center text-left"
              onClick={() => walletsMenu.setOpen((v) => !v)}
            >
              <span className="font-bold text-content-inverse-muted">
                {formatMoney(firstWallet.balance)}{' '}
                {firstWallet.currency?.ico ?? `#${firstWallet.currency_id}`}
              </span>
              <span className="text-xs font-normal leading-4 text-content-inverse-accent">{t('profit.walletsMenu')}</span>
            </button>

            {walletsMenu.open && (
              <div className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-md border border-line-default bg-surface-card py-2 shadow-base">
                {wallets.map((w) => (
                  <Link
                    key={w.id}
                    to={`/payments?wallet_id=${w.id}`}
                    className="block px-4 py-2 text-sm font-normal leading-5 text-content-primary hover:bg-surface-hover"
                    onClick={() => walletsMenu.setOpen(false)}
                  >
                    {formatMoney(w.balance)} {w.currency?.ico ?? `#${w.currency_id}`}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User dropdown — Профиль / 2FA / Выйти. */}
        <div ref={userMenu.ref} className="relative flex h-full items-center pl-6 pr-4">
          <button
            type="button"
            className="flex items-center justify-center gap-2"
            onClick={() => userMenu.setOpen((v) => !v)}
          >
            <Icon name="User_Big" />
            <div className="font-bold text-content-inverse-muted">
              <span>{user ? user.username : 'user_'}</span>
            </div>
            <span className="text-content-inverse-accent">▾</span>
          </button>

          {userMenu.open && (
            <div className="absolute right-4 top-full z-50 mt-1 min-w-[200px] rounded-md border border-line-default bg-surface-card py-2 shadow-base">
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm font-normal leading-5 text-content-primary hover:bg-surface-hover"
                onClick={handleNavigate('/settings', () => userMenu.setOpen(false))}
              >
                {t('profile')}
              </button>
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm font-normal leading-5 text-content-primary hover:bg-surface-hover"
                onClick={handleNavigate('/settings/2fa', () => userMenu.setOpen(false))}
              >
                {t('nav:twoFactor')}
              </button>
              <div className="my-1 border-t border-line-subtle" />
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm font-normal leading-5 text-content-primary hover:bg-surface-hover"
                onClick={handleLogout}
              >
                {t('logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
